import React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import LazyImage from "@insite/mobius/LazyImage";
import { css } from "styled-components";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Divider from "@WhiteCap/Components/Basic/Divider";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Zone from "@insite/client-framework/Components/Zone";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import GenrateUrl from "@WhiteCap/Components/Basic/GenrateUrl";

const enum fields {
    title = "title",
    categories = "categories",
    mainCategories = "mainCategories",
}
export interface FastenersAnchorsCategoryModel {
    fields: {
        name: string;
        image: string;
        linkText: string;
        link: LinkFieldValue;
        openInNewWindow: boolean;
        isMenuItem: boolean;
    };
}
export interface MainFastenersAnchorsCategoryModel {
    fields: {
        mainCategoryTitle: string;
        [fields.categories]: FastenersAnchorsCategoryModel[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.mainCategories]: MainFastenersAnchorsCategoryModel[];
    };
}

export interface FastenersAnchorsStyles {
    container?: InjectableCss;
    mainGridContainer?: GridContainerProps;
    mainGridItem?: GridItemProps;
    title?: TypographyProps;
    listGridContainer?: GridContainerProps;
    listGridItem?: GridItemProps;
    titlelink?: LinkProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyProps;
    mainCategoryTitleContainer?: InjectableCss;
    mainCategoryTitle?: TypographyProps;
    productContainer?: InjectableCss;
    imageContainer?: InjectableCss;
    contentGridItem?: GridItemProps;
    link?: LinkPresentationProps;
}

const fastenersAnchorsStyle: FastenersAnchorsStyles = {
    container: {
        css: css`
            font-family: "Open Sans", "Helvetica Neue", Helvetica, Arial, sans-serif;
        `,
    },
    mainGridContainer: {
        css: css`
            margin-top: 50px;
        `,
    },
    mainGridItem: {
        width: [12, 12, 3, 3, 3],
        css: css`
            padding: 20px 0px;
            margin-top: 10px;
        `,
    },
    mainCategoryTitleContainer: {
        css: css`
            margin-top: 20px;
        `
    },
    mainCategoryTitle: {
        css: css`
            font-weight: 300;
            line-height: 1.1;
            font-size: 25px;
            margin-top: 0rem;
            margin-bottom: 2rem;
        `,
    },
    listGridContainer: {
        css: css`
            padding: 20px;
        `,
    },
    listGridItem: {
        width: [12, 12, 2, 2, 2],
        css: css`
            padding-top: 3px;
            padding-bottom: 3px;
        `,
    },
    titlelink: {
        color: WhitecapColors.orangeLink,
        css: css`
            font-size: 14px;
            width: 100%;
            ${TypographyStyle} {
                width: 100%;
                text-align: center;
            }
            &:hover {
                text-decoration: underline;
                ${TypographyStyle} {
                    color: ${WhitecapColors.lightBlack};
                }
            }
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    heading: {
        css: css`
            font-size: 18px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            line-height: 1.5;
            text-align: center;
            width: 100%;
        `,
    },
    contentGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    productContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
        `,
    },
    imageContainer: {
        css: css`
            min-height: 175px;
        `,
    },

    link: {
        color: WhitecapColors.orangeLink,
        css: css`
            font-size: 14px;

            &:hover {
                text-decoration: underline;
                ${TypographyStyle} {
                    color: ${WhitecapColors.lightBlack};
                }
            }
        `,
    },
    title: {
        css: css`
            font-size: 18px;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 1px;
            width: 100%;
            text-align: center;
        `,
    },
};

const styles = fastenersAnchorsStyle;

const SafetySystem = ({ id, fields }: OwnProps) => {
    const categories = (
        fields.mainCategories.map(item => {
            return item.fields.categories.filter(item => item.fields.isMenuItem);
        }) as Array<Array<FastenersAnchorsCategoryModel>>
    ).reduce((acc, x) => acc.concat(x), []);
    console.log(categories);
    return (
        <StyledWrapper {...styles.container}>
            <Typography variant="h4" {...styles.title}>
                {fields.title}
            </Typography>
            {console.log(fields)}
            <Divider borderTop={`1px solid ${WhitecapColors.borderGray}`} />
            <GridContainer {...styles.listGridContainer}>
                {categories &&
                    categories.map(item => (
                        <GridItem {...styles.listGridItem} key={item.fields.name}>
                            <Link href={`#${item.fields.name.replace(" ", "-")}`} {...styles.titlelink}>
                                <Typography as="strong">{item.fields.name}</Typography>
                            </Link>
                        </GridItem>
                    ))}
            </GridContainer>
            <Zone contentId={id} zoneName="feature-supplier" />
            {fields.mainCategories &&
                fields.mainCategories.map(items => (
                    <>
                        <StyledWrapper {...styles.mainCategoryTitleContainer}>
                            <Typography {...styles.mainCategoryTitle}>{items.fields.mainCategoryTitle}</Typography>
                        </StyledWrapper>
                        <GridContainer {...styles.mainGridContainer}>
                            {items.fields.categories &&
                                items.fields.categories.map(item => (
                                    <GridItem {...styles.mainGridItem} key={item.fields.name}>
                                        <GridContainer id={`${item.fields.name.replace(" ", "-")}`}>
                                            <GridItem {...styles.headingGridItem}>
                                                <Typography variant="h3" {...styles.heading}>
                                                    {item.fields.name}
                                                </Typography>
                                                <Divider borderTop={`1px solid ${WhitecapColors.borderGray}`} />
                                            </GridItem>

                                            <GridItem {...styles.contentGridItem}>
                                                <StyledWrapper {...styles.productContainer}>
                                                    <StyledWrapper {...styles.imageContainer}>
                                                        <LazyImage src={item.fields.image} />
                                                    </StyledWrapper>

                                                    <GenrateUrl
                                                        link={item.fields.link}
                                                        style={styles.link}
                                                        openInNewTab={item.fields.openInNewWindow}
                                                    >
                                                        {item.fields.linkText}
                                                    </GenrateUrl>
                                                </StyledWrapper>
                                            </GridItem>
                                        </GridContainer>
                                    </GridItem>
                                ))}
                        </GridContainer>
                    </>
                ))}
        </StyledWrapper>
    );
};

const SafetySystemModule: WidgetModule = {
    component: SafetySystem,
    definition: {
        displayName: "Popular categories and Product List",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.mainCategories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.mainCategoryTitle;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "mainCategoryTitle",
                        displayName: "Main Category title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        placeholder: "Fasteners, Anchors, etc",
                    },

                    {
                        name: fields.categories,
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields) => {
                            return item.fields.name;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "name",
                                displayName: "Category Name",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },

                            {
                                name: "image",
                                displayName: "Icon Image",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                            },

                            {
                                name: "linkText",
                                displayName: "Link Text",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "link",
                                displayName: "Link",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Url",
                                },
                            },
                            {
                                name: "openInNewWindow",
                                editorTemplate: "CheckboxField",
                                defaultValue: true,
                            },
                            {
                                name: "isMenuItem",
                                displayName: "Show in category menu",
                                editorTemplate: "CheckboxField",
                                defaultValue: true,
                            },
                        ],
                    },
                ],
            },
        ],

        group: "Common",
    },
};

export default SafetySystemModule;
